import { useContext } from "react";
import { Alert, Button, Snackbar } from "@mui/material";
import { CallV3Context } from "../state/CallV3Context";
import { addBreadcrumb } from "../config/sentry";

// GMA-1331: render any active CallV3Context.placeCallError as a non-auto-
// hiding MUI snackbar so the caller always sees what went wrong. The retry
// action for popup-blocked must run window.open synchronously inside the
// click handler (preserving the user-gesture) so the browser allows it.
const CallErrorSnackbar = () => {
  const {
    placeCallError,
    clearPlaceCallError,
    placeCall,
    callId,
    callerName,
    inCall,
  } = useContext(CallV3Context);

  if (!placeCallError) return null;
  // #808: CallReceiver shows answer errors, and a blocked call window with
  // its recovery, inside its modal while it is open (same condition as its
  // Modal); the modal hides this snackbar from assistive technology, so
  // showing both would only duplicate it visually.
  const isAnswerError = placeCallError.kind.startsWith("answer-");
  const receiverOpen = !!(callId && callerName && !inCall);
  if ((isAnswerError || placeCallError.kind === "popup-blocked") && receiverOpen) {
    return null;
  }

  const handleRetryPopup = () => {
    const url = placeCallError.retryUrl;
    // NB: no "noopener"/"noreferrer" — that returns null even on success, so
    // this retry would open the window but never focus it or clear the error
    // (and each click would spawn another). opener is nulled manually below.
    const popup = window.open(url, "_blank");
    const popupBlocked = !popup;
    addBreadcrumb("call.openCall", {
      category: "call.lifecycle",
      level: popupBlocked ? "warning" : "info",
      stage: "user-retry",
      popupBlocked,
      url,
    });
    if (popup) {
      popup.opener = null;
      popup.focus();
      clearPlaceCallError();
    }
  };

  const handleRetryInit = async () => {
    await placeCall();
  };

  const severity = placeCallError.kind === "popup-blocked" ? "warning" : "error";
  // #808: answer failures get no Retry — it runs placeCall, which would start
  // an outgoing call. The incoming-call modal's Answer button is the retry.
  const action = isAnswerError ? null :
    placeCallError.kind === "popup-blocked" ? (
      <Button color="inherit" size="small" onClick={handleRetryPopup}>
        Open call window
      </Button>
    ) : (
      <Button color="inherit" size="small" onClick={handleRetryInit}>
        Retry
      </Button>
    );

  return (
    <Snackbar
      open={true}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      data-testid="call-error-snackbar"
    >
      <Alert
        severity={severity}
        onClose={clearPlaceCallError}
        action={action}
        sx={{ width: "100%" }}
      >
        {placeCallError.message}
      </Alert>
    </Snackbar>
  );
};

export default CallErrorSnackbar;
